import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, fileUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronLeft, MapPin, Users, Heart, MessageCircle, Shield, Calendar, Twitch, Youtube, Music, Save, Loader2, MessageSquare, ImagePlus, Store } from 'lucide-react';
import { toast } from 'sonner';
import HorizontalScrollRow from '@/components/HorizontalScrollRow';

function SocialsCard() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const [socials, setSocials] = useState({ discord: '', twitch: '', youtube: '', tiktok: '' });
  const [name, setName] = useState('');
  const [gamerTag, setGamerTag] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      setSocials({ discord: '', twitch: '', youtube: '', tiktok: '', ...(user.socials || {}) });
      setName(user.name || '');
      setGamerTag(user.gamer_tag || '');
    }
  }, [user]);

  const save = async () => {
    setLoading(true);
    try {
      await api.patch('/users/me', { name, gamer_tag: gamerTag, socials });
      await refresh();
      toast.success(t('profile.edit.saved'));
    } catch (e) { toast.error(e.response?.data?.detail || t('common.error')); }
    finally { setLoading(false); }
  };

  const socialFields = [
    { key: 'discord',  icon: MessageSquare, color: '#5865F2' },
    { key: 'twitch',   icon: Twitch,        color: '#9146FF' },
    { key: 'youtube',  icon: Youtube,       color: '#FF0000' },
    { key: 'tiktok',   icon: Music,         color: '#00F2EA' },
  ];

  return (
    <div className="mt-6 glass rounded-2xl p-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-xl uppercase tracking-tighter">{t('profile.socials.title')}</h2>
          <p className="text-white/50 text-xs mt-1">{t('profile.socials.subtitle')}</p>
        </div>
        <Button
          data-testid="profile-save-btn"
          onClick={save}
          disabled={loading}
          className="rounded-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide h-10"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> {t('profile.edit.save')}</>}
        </Button>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-white/70 text-xs uppercase tracking-wider">{t('profile.edit.gamer_tag')}</Label>
          <Input
            data-testid="profile-gamertag-input"
            value={gamerTag}
            onChange={(e) => setGamerTag(e.target.value)}
            placeholder="ApexHunter_FR"
            className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
        <div>
          <Label className="text-white/70 text-xs uppercase tracking-wider">{t('profile.edit.name')}</Label>
          <Input
            data-testid="profile-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Mon pseudo"
            className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
        {socialFields.map((s) => (
          <div key={s.key}>
            <Label className="text-white/70 text-xs uppercase tracking-wider inline-flex items-center gap-1.5">
              <s.icon className="w-3 h-3" style={{ color: s.color }} /> {s.key.charAt(0).toUpperCase() + s.key.slice(1)}
            </Label>
            <Input
              data-testid={`profile-social-${s.key}`}
              value={socials[s.key] || ''}
              onChange={(e) => setSocials({ ...socials, [s.key]: e.target.value })}
              placeholder={t(`profile.socials.placeholder.${s.key}`)}
              className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, value, label, color }) {
  return (
    <div className="glass rounded-2xl p-5">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: `${color}22`, color }}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display font-black text-3xl tracking-tighter leading-none">{value ?? 0}</div>
          <div className="text-[11px] uppercase tracking-wider text-white/50 mt-1">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [mediaPosts, setMediaPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) return;
    if (!user || user === false) {
      navigate('/');
      return;
    }
    api.get('/users/me/profile')
      .then(({ data }) => setProfile(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get('/media', { params: { user_id: user.id, sort: 'recent' } })
      .then(({ data }) => setMediaPosts(data || []))
      .catch(() => setMediaPosts([]));
  }, [user, navigate]);

  if (user === null || loading) {
    return <div className="min-h-screen grid place-items-center text-white/60">Chargement...</div>;
  }
  if (!profile) {
    return <div className="min-h-screen grid place-items-center text-white/60">Profil indisponible.</div>;
  }

  const { stats, spots } = profile;
  const u = profile.user;
  // All map categories with their visual identity (kept in sync with MapView legend)
  const CATEGORIES = [
    { id: 'car_meet',    label: 'Car Meet',   short: 'CM',      color: '#00F0FF' },
    { id: 'viewpoint',   label: 'Point de vue', short: 'Vue',   color: '#9DEDFF' },
    { id: 'parking',     label: 'Parking',    short: 'Parking', color: '#FFD479' },
    { id: 'gas_station', label: 'Station',    short: 'Essence', color: '#10B981' },
    { id: 's365',        label: '365',        short: '365',     color: '#FF7A00' },
    { id: 'spot',        label: 'Spot',       short: 'Spot',    color: '#A78BFA' },
  ];
  const catInfo = (id) => CATEGORIES.find((c) => c.id === id) || { id, label: id, short: id, color: '#FFFFFF' };
  const countsByCat = CATEGORIES.map((c) => ({ ...c, count: spots.filter((s) => s.category === c.id).length }));

  return (
    <div className="min-h-screen pt-24 pb-24 px-6 bg-[#0A0A0A] text-white">
      <div className="max-w-5xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-white">
          <ChevronLeft className="w-3 h-3" /> Accueil
        </Link>

        {/* Hero card */}
        <div className="mt-4 glass rounded-3xl p-6 md:p-8 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-30" style={{ background: '#FF007F' }} />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: '#00F0FF' }} />
          <div className="relative flex flex-col md:flex-row md:items-center gap-6">
            <label
              data-testid="profile-avatar-upload"
              className="relative w-24 h-24 rounded-2xl overflow-hidden cursor-pointer group"
              title={t('profile.avatar.change')}
            >
              {user.avatar_url ? (
                <img src={fileUrl(user.avatar_url)} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center font-display font-black text-4xl text-black">
                  {(u.name || u.email)[0].toUpperCase()}
                </div>
              )}
              <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition grid place-items-center text-white text-[10px] font-bold uppercase tracking-wider text-center px-2">
                {t('profile.avatar.change')}
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const fd = new FormData();
                  fd.append('file', f);
                  try {
                    await api.post('/users/me/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    await refresh();
                    toast.success(t('profile.avatar.saved'));
                  } catch (err) {
                    toast.error(err.response?.data?.detail || t('common.error'));
                  }
                }}
              />
            </label>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tighter">{u.name}</h1>
                {u.role === 'admin' && (
                  <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border border-[#FF007F] text-[#FF007F] bg-[#FF007F]/10 inline-flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Admin
                  </span>
                )}
              </div>
              <div className="text-white/60 text-sm mt-1">{u.email}</div>
              <div className="text-white/40 text-xs mt-2 inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Membre de Horizon.Meet&Cruise
              </div>
            </div>
            <Link to="/maps/fh6">
              <Button
                data-testid="profile-go-map-btn"
                className="rounded-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide h-11 px-5"
              >
                <MapPin className="w-4 h-4 mr-2" /> Ajouter un spot
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <SocialsCard />
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="profile-stats">          <StatTile icon={MapPin} value={stats.spots_count} label="Spots créés" color="#FF007F" />
          <StatTile icon={Users} value={stats.car_meets} label="Car Meets" color="#00F0FF" />
          <StatTile icon={MessageCircle} value={stats.comments_count} label="Commentaires" color="#9DEDFF" />
          <StatTile icon={Heart} value={stats.likes_received} label="Likes reçus" color="#FFD479" />
        </div>

        {/* Spots list */}
        <div className="mt-10">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="font-display text-2xl lg:text-3xl font-black uppercase tracking-tighter">Mes spots</h2>
              <p className="text-white/50 text-sm mt-1">
                {stats.spots_count} contribution{stats.spots_count > 1 ? 's' : ''} sur la carte de FH6.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs flex-wrap justify-end" data-testid="profile-cat-summary">
              {countsByCat.map((c) => (
                <div key={c.id} className="inline-flex items-center gap-1.5 text-white/60" data-testid={`profile-cat-${c.id}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: c.color, boxShadow: `0 0 8px ${c.color}` }} />
                  {c.count} {c.short}
                </div>
              ))}
            </div>
          </div>

          {spots.length === 0 ? (
            <div className="glass rounded-2xl p-10 text-center" data-testid="profile-empty">
              <MapPin className="w-8 h-8 mx-auto text-white/30" />
              <div className="text-white/70 font-display font-bold text-lg mt-3">Aucun spot pour l'instant</div>
              <div className="text-white/40 text-sm mt-1 mb-5">
                Lance-toi : ouvre la map et place ton premier car meet ou point photo.
              </div>
              <Link to="/maps/fh6">
                <Button className="rounded-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide">
                  Ouvrir la map
                </Button>
              </Link>
            </div>
          ) : (
            <HorizontalScrollRow testId="profile-spots-row" ariaLabel="Mes spots">
              {spots.map((s) => (
                <Link
                  key={s.id}
                  to={`/maps/fh6`}
                  data-testid={`profile-spot-${s.id}`}
                  className="snap-start shrink-0 w-[280px] group glass rounded-2xl overflow-hidden hover:border-white/30 transition-all"
                >
                  <div className="relative h-36 bg-black/40">
                    {s.image_path ? (
                      <img src={fileUrl(s.image_path)} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-white/20">
                        {s.category === 'car_meet' ? <Users className="w-8 h-8" /> : <Camera className="w-8 h-8" />}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    {(() => {
                      const ci = catInfo(s.category);
                      return (
                        <span
                          data-testid={`profile-spot-badge-${s.id}`}
                          className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border backdrop-blur-md"
                          style={{
                            borderColor: ci.color,
                            color: ci.color,
                            background: 'rgba(0,0,0,0.5)',
                          }}
                        >
                          {ci.label}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="p-3">
                    <div className="font-display font-bold text-sm uppercase tracking-tight truncate">{s.title}</div>
                    <div className="text-[11px] text-white/40 mt-1 line-clamp-1">{s.description || '—'}</div>
                    <div className="flex items-center gap-3 text-[11px] text-white/50 mt-2">
                      <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" />{s.likes_count}</span>
                      <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" />{s.comments_count || 0}</span>
                      <span className="ml-auto">{new Date(s.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </HorizontalScrollRow>
          )}
        </div>

        {/* Publications (Media: screenshots + vidéos) */}
        {mediaPosts.length > 0 && (
          <div className="mt-10" data-testid="profile-media">
            <h2 className="font-display text-2xl lg:text-3xl font-black uppercase tracking-tighter inline-flex items-center gap-2">
              <ImagePlus className="w-5 h-5 text-[#FF007F]" /> Mes publications
              <span className="text-white/40 text-base font-bold ml-1">({mediaPosts.length})</span>
            </h2>
            <div className="text-white/50 text-sm mt-1 mb-4">Screenshots, vidéos et liens partagés.</div>
            <HorizontalScrollRow testId="profile-media-row" ariaLabel="Mes publications">
              {mediaPosts.map((m) => (
                <Link
                  key={m.id}
                  to="/media"
                  data-testid={`profile-media-${m.id}`}
                  className="snap-start shrink-0 w-[280px] group glass rounded-2xl overflow-hidden hover:border-white/30 transition-all"
                >
                  <div className="relative aspect-video bg-black/40">
                    {m.kind === 'image' && m.image_path && (
                      <img src={fileUrl(m.image_path)} alt={m.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    )}
                    {m.kind === 'video' && m.video_path && (
                      <video src={fileUrl(m.video_path)} muted className="w-full h-full object-cover" />
                    )}
                    {m.kind === 'link' && (() => {
                      try {
                        const u = new URL(m.external_url || '');
                        if (u.hostname.includes('youtu')) {
                          const id = u.hostname.includes('youtu.be') ? u.pathname.slice(1) : u.searchParams.get('v');
                          if (id) return <img src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`} alt={m.title} className="w-full h-full object-cover" />;
                        }
                      } catch (_e) { /* noop */ }
                      return <div className="w-full h-full grid place-items-center text-[#00F0FF]"><ImagePlus className="w-8 h-8" /></div>;
                    })()}
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 text-[9px] uppercase tracking-widest font-bold text-white border border-white/10">
                      {m.kind === 'image' ? 'Screenshot' : m.kind === 'video' ? 'Vidéo' : 'Vidéo (lien)'}
                    </span>
                  </div>
                  <div className="p-3">
                    <div className="font-display font-bold text-sm uppercase tracking-tight truncate">{m.title}</div>
                    <div className="flex items-center gap-3 text-[11px] text-white/50 mt-2">
                      <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" />{m.likes_count || 0}</span>
                      <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" />{m.comments_count || 0}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </HorizontalScrollRow>
          </div>
        )}
      </div>
    </div>
  );
}
